import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  ProgressEvent,
  ProgressState,
} from '../components/OperationProgress/types';

export type OTCOperationType = 'sync' | 'checkDifferences' | 'upload';

interface OperationState {
  isActive: boolean;
  totalOtcs: number;
  processed: number;
  success: number;
  failed: number;
  errors: string[];
  status: 'idle' | 'in_progress' | 'complete';
}

interface OTCProgressStore {
  operations: Record<OTCOperationType, OperationState>;
  start: (type: OTCOperationType) => void;
  updateFromEvent: (type: OTCOperationType, event: ProgressEvent) => void;
  complete: (type: OTCOperationType) => void;
  reset: (type: OTCOperationType) => void;
}

const initialOperationState: OperationState = {
  isActive: false,
  totalOtcs: 0,
  processed: 0,
  success: 0,
  failed: 0,
  errors: [],
  status: 'idle',
};

export const useOTCProgressStore = create<OTCProgressStore>()(
  devtools(
    set => ({
      operations: {
        sync: { ...initialOperationState },
        checkDifferences: { ...initialOperationState },
        upload: { ...initialOperationState },
      },

      start: (type: OTCOperationType) =>
        set(state => ({
          operations: {
            ...state.operations,
            [type]: {
              isActive: true,
              totalOtcs: 0,
              processed: 0,
              success: 0,
              failed: 0,
              errors: [],
              status: 'in_progress',
            },
          },
        })),

      updateFromEvent: (type: OTCOperationType, event: ProgressEvent) =>
        set(state => ({
          operations: {
            ...state.operations,
            [type]: {
              totalOtcs: event.total_otcs,
              processed: event.processed,
              success: event.success,
              failed: event.failed,
              errors: event.errors,
              status: event.status === 'complete' ? 'complete' : 'in_progress',
              isActive: event.status !== 'complete',
            },
          },
        })),

      complete: (type: OTCOperationType) =>
        set(state => ({
          operations: {
            ...state.operations,
            [type]: {
              ...state.operations[type],
              status: 'complete',
              isActive: false,
            },
          },
        })),

      reset: (type: OTCOperationType) =>
        set(state => ({
          operations: {
            ...state.operations,
            [type]: { ...initialOperationState },
          },
        })),
    }),
    { name: 'otc-progress' }
  )
);

// Convenience hook for sync operations - returns ProgressState compatible interface
export const useOTCSyncProgress = (): ProgressState => {
  const store = useOTCProgressStore();
  return {
    ...store.operations.sync,
    start: () => store.start('sync'),
    updateFromEvent: (event: ProgressEvent) =>
      store.updateFromEvent('sync', event),
    complete: () => store.complete('sync'),
    reset: () => store.reset('sync'),
  };
};

// Convenience hook for check differences operations - returns ProgressState compatible interface
export const useOTCCheckDifferencesProgress = (): ProgressState => {
  const store = useOTCProgressStore();
  return {
    ...store.operations.checkDifferences,
    start: () => store.start('checkDifferences'),
    updateFromEvent: (event: ProgressEvent) =>
      store.updateFromEvent('checkDifferences', event),
    complete: () => store.complete('checkDifferences'),
    reset: () => store.reset('checkDifferences'),
  };
};

// Convenience hook for upload operations - returns ProgressState compatible interface
export const useOTCUploadProgress = (): ProgressState => {
  const store = useOTCProgressStore();
  return {
    ...store.operations.upload,
    start: () => store.start('upload'),
    updateFromEvent: (event: ProgressEvent) =>
      store.updateFromEvent('upload', event),
    complete: () => store.complete('upload'),
    reset: () => store.reset('upload'),
  };
};
